import { useState } from 'react';
import axios from 'axios';
import { INDUSTRY_PROMPTS } from '../data/industryPrompts';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIGenerateModal({ user, platforms, onClose }) {
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedImage, setGeneratedImage] = useState('');
  const [contentType, setContentType] = useState('none'); // none, facts, quotes, safety, tips, cost, time
  const [showEducationalSection, setShowEducationalSection] = useState(false); // Toggle for educational section
  const [educationalContent, setEducationalContent] = useState('');
  const [imageStyle, setImageStyle] = useState('realistic');
  const [mood, setMood] = useState('professional');
  const [focus, setFocus] = useState('environment');
  const [smartVariety, setSmartVariety] = useState(true);
  const [advancedMode, setAdvancedMode] = useState('auto'); // 'auto' or 'manual'
  const [loading, setLoading] = useState({ content: false, image: false, saving: false, educational: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Use current_industry for admin switcher, fallback to industry
  const effectiveIndustry = user.current_industry || user.industry;

  const generateContent = async () => {
    setLoading({ ...loading, content: true });
    setError('');

    try {
      const response = await axios.post(`${API}/generate/content`, {
        user_id: user.id,
        industry: effectiveIndustry,
        platform: 'General',
        prompt: customPrompt || 'Generate engaging social media content'
      });
      setGeneratedContent(response.data.content);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate content');
    } finally {
      setLoading({ ...loading, content: false });
    }
  };

  const generateEducationalContent = async (type) => {
    setLoading({ ...loading, educational: true });
    try {
      console.log(`Generating ${type} content for ${effectiveIndustry}`);
      const response = await axios.get(`${API}/content/generate/${effectiveIndustry}/${type}`);
      console.log('Generated content:', response.data);
      setEducationalContent(response.data.formatted);
    } catch (error) {
      console.error('Error generating educational content:', error);
      setEducationalContent('');
    } finally {
      setLoading({ ...loading, educational: false });
    }
  };

  const generateImage = async () => {
    setLoading({ ...loading, image: true });
    setError('');

    try {
      // Logic: AI generates image WITH text beautifully integrated (no overlay after)
      let imagePrompt;
      
      if (customPrompt && customPrompt.trim()) {
        // User provided custom prompt
        imagePrompt = customPrompt;
      } else if (educationalContent && educationalContent.trim()) {
        // Fact selected - create prompt with VARIED typography styles
        const cleanFact = educationalContent.replace(/🔍 Did You Know\?\n\n/, '').replace(/💡|💬|🛡️|✨|💰|⏰|⭐|🚀/g, '').replace(/Fact:|Quote:|Safety:|Pro Tip:|Cost Saving:|Time Management:|Best Practice:|Efficiency:/g, '').trim();
        
        // Random typography styles for variety
        const styles = [
          `Create a modern ${effectiveIndustry} quote card with MINIMALIST design. White background with "${cleanFact}" in large, bold black serif font centered. Simple geometric accent lines. Clean, elegant typography. Professional magazine style.`,
          
          `Create a vibrant ${effectiveIndustry} social post. Colorful gradient background (orange to pink). "${cleanFact}" in white bold sans-serif font at the top with subtle glow effect. Modern, eye-catching Instagram style.`,
          
          `Create an artistic ${effectiveIndustry} image. Background: relevant scene. "${cleanFact}" overlaid in center using large, bold typography on a semi-transparent colorful card (blue/purple). Modern glassmorphism style with blur.`,
          
          `Create a professional ${effectiveIndustry} quote. Dark background with subtle industry-related texture. "${cleanFact}" in elegant gold/yellow font on the left side with vertical accent line. Sophisticated business style.`,
          
          `Create a creative ${effectiveIndustry} post. Background: relevant image. "${cleanFact}" displayed diagonally or at an angle in bold, modern font with colorful shadow. Dynamic, trendy social media style.`,
          
          `Create a classic ${effectiveIndustry} card. Industry background with blur. "${cleanFact}" in classic serif font inside an elegant border frame (rounded rectangle). Traditional quote card style with decorative corners.`,
          
          `Create a bold ${effectiveIndustry} image. Solid color background (choose complementary color). "${cleanFact}" in huge, extra bold typography covering most of image. Minimalist, powerful statement style.`,
          
          `Create a modern ${effectiveIndustry} design. Split screen: left side industry image, right side colored panel. "${cleanFact}" on colored panel in clean, modern font. Magazine editorial style.`
        ];
        
        // Randomly select a style
        const randomStyle = styles[Math.floor(Math.random() * styles.length)];
        imagePrompt = randomStyle;
      } else {
        // Generic prompt
        imagePrompt = `Professional image for ${effectiveIndustry} social media post`;
      }
      
      // DEBUG: Log what we're sending
      console.log('=== GENERATING IMAGE ===');
      console.log('Educational Content:', educationalContent);
      console.log('Custom Prompt:', customPrompt);
      console.log('Image Prompt being sent:', imagePrompt);
      
      const response = await axios.post(`${API}/generate/image/fast`, {
        user_id: user.id,
        industry: effectiveIndustry,
        prompt: imagePrompt,
        overlay_text: null,  // No overlay - AI generates the text in the image itself
        image_style: imageStyle,
        mood: mood,
        focus: focus,
        smart_variety: smartVariety
      });
      setGeneratedImage(`data:${response.data.mime_type};base64,${response.data.image_base64}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate image');
    } finally {
      setLoading({ ...loading, image: false });
    }
  };

  const savePost = async () => {
    if (!generatedContent && !generatedImage) {
      setError('Please generate content or image first');
      return;
    }

    setLoading({ ...loading, saving: true });
    setError('');

    try {
      // Step 1: Create empty post with General platform (user can change later)
      const postResponse = await axios.post(`${API}/posts`, {
        user_id: user.id,
        platform: 'General',
        industry: user.current_industry || user.industry
      });

      // Step 2: Update post with content and image
      await axios.put(`${API}/posts/${postResponse.data.id}`, {
        content: generatedContent || '',
        image_base64: generatedImage ? generatedImage.split(',')[1] : null
      });

      setSuccess('✅ Post saved successfully!');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setLoading({ ...loading, saving: false });
    }
  };
  
  const industryPrompts = INDUSTRY_PROMPTS[effectiveIndustry] || [];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-teal-500/30 rounded-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold gradient-text">AI Generate Content</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl">×</button>
        </div>

        {/* Educational Content Toggle */}
        <div className="mb-4">
          <div 
            onClick={() => {
              const newState = !showEducationalSection;
              setShowEducationalSection(newState);
              // Clear content when toggled off
              if (!newState) {
                setEducationalContent('');
                setContentType('none');
              }
            }}
            className="flex items-center justify-between px-4 py-3 bg-blue-900/20 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-900/30 transition"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">📚</span>
              <span className="text-sm font-medium text-blue-200">Educational Content</span>
              <span className="text-xs text-blue-400">(Optional)</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-blue-300">
                {showEducationalSection ? 'ON' : 'OFF'}
              </span>
              <div className={`w-12 h-6 rounded-full transition ${
                showEducationalSection ? 'bg-blue-500' : 'bg-gray-600'
              } relative`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  showEducationalSection ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </div>
            </div>
          </div>

          {/* Collapsible Content */}
          {showEducationalSection && (
            <div className="mt-3 p-4 bg-blue-900/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-300 mb-3">AI generates diverse content: Facts, Quotes, Safety Tips, Cost Saving, Time Management & more!</p>
              <button
                onClick={() => {
                  setContentType('facts');
                  generateEducationalContent('facts');
                  setShowPrompts(false);
                }}
                disabled={loading.educational}
                className="px-4 py-2 rounded-lg border-2 border-blue-500 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition text-sm font-medium"
              >
                <span className="text-lg mr-2">💡</span>
                Generate Content
              </button>
              
              {loading.educational && (
                <p className="text-xs text-blue-400 mt-3">⏳ Generating diverse content...</p>
              )}
              {educationalContent && (
                <div className="mt-3 bg-gray-900 border border-blue-500/50 rounded p-3">
                  <p className="text-sm text-white whitespace-pre-line">{educationalContent}</p>
                  <button
                    onClick={() => {
                      generateEducationalContent('facts');
                    }}
                    disabled={loading.educational}
                    className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition"
                  >
                    🔄 Generate Different Content
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Advanced AI Controls */}
        <div className="mb-4">
          <div className="flex items-center justify-between w-full px-4 py-2 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/50 rounded-lg">
            <span className="text-sm font-medium text-purple-200">
              🎨 Advanced AI Controls
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAdvancedMode('auto')}
                className={`px-3 py-1 rounded text-xs font-medium transition ${
                  advancedMode === 'auto'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                🤖 Auto
              </button>
              <button
                onClick={() => {
                  setAdvancedMode('manual');
                  setShowAdvanced(true);
                }}
                className={`px-3 py-1 rounded text-xs font-medium transition ${
                  advancedMode === 'manual'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                ⚙️ Manual
              </button>
            </div>
          </div>

          {advancedMode === 'manual' && (
            <div className="mt-3 p-4 bg-gray-800/50 border border-purple-500/30 rounded-lg space-y-4">
              {/* Image Style */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">🖼️ Image Style</label>
                <select
                  value={imageStyle}
                  onChange={(e) => setImageStyle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="realistic">📸 Realistic Photo</option>
                  <option value="illustration">🎨 Illustration/Graphic</option>
                  <option value="quote">📰 Quote Card/Typography</option>
                  <option value="minimalist">⚪ Minimalist/Modern</option>
                </select>
              </div>

              {/* Mood/Tone */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">🎭 Mood/Tone</label>
                <select
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="professional">💼 Professional & Serious</option>
                  <option value="friendly">😊 Friendly & Approachable</option>
                  <option value="inspiring">✨ Inspiring & Motivational</option>
                  <option value="educational">📚 Educational & Informative</option>
                  <option value="modern">🚀 Modern & Trendy</option>
                </select>
              </div>

              {/* Focus Type */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-2">🎯 Image Focus</label>
                <div className="grid grid-cols-3 gap-2">
                  {['people', 'objects', 'environment'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFocus(f)}
                      className={`px-3 py-2 rounded-lg border-2 transition text-xs ${
                        focus === f
                          ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                          : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-purple-500/50'
                      }`}
                    >
                      {f === 'people' && '👥'} {f === 'objects' && '🔧'} {f === 'environment' && '🏞️'}
                      <br />
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Smart Variety Toggle */}
              <div className="flex items-center justify-between p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                <div>
                  <h5 className="text-white text-sm font-medium">🔄 Smart Variety Mode</h5>
                  <p className="text-xs text-gray-400">Auto-rotate scenes for diversity</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smartVariety}
                    onChange={(e) => setSmartVariety(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Industry Prompts Quick Select - Hidden when Educational Content is active */}
        {contentType === 'none' && (
          <div className="mb-4">
            <button
              onClick={() => setShowPrompts(!showPrompts)}
              className="flex items-center justify-between w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:border-orange-500 transition text-left"
            >
              <span className="text-sm font-medium text-gray-300">
                💡 Quick Prompts for {effectiveIndustry}
              </span>
              <span className="text-gray-400">{showPrompts ? '▲' : '▼'}</span>
            </button>
          
            {showPrompts && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700 max-h-60 overflow-y-auto">
                {industryPrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCustomPrompt(prompt);
                      setShowPrompts(false);
                    }}
                    className="px-3 py-2 text-left text-sm bg-gray-700 hover:bg-orange-500/20 hover:border-orange-500 border border-gray-600 rounded-lg transition text-gray-300 hover:text-white"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Custom Prompt - Optional */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            ✍️ Custom Prompt <span className="text-xs text-gray-500">(Optional - Leave blank for AI to decide)</span>
          </label>
          <textarea
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition"
            rows="3"
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Optional: Add specific instructions for AI, or leave blank..."
          />
        </div>
        
        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <button
            onClick={generateContent}
            disabled={loading.content}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed btn-glow"
          >
            {loading.content ? 'Generating...' : '✨ Generate Text'}
          </button>
          <button
            onClick={generateImage}
            disabled={loading.image}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed btn-glow"
          >
            {loading.image ? 'Generating...' : '🎨 Generate Image'}
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Generated Content Preview */}
        {(generatedContent || generatedImage) && (
          <div className="border-t border-orange-500/20 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>
            
            {generatedContent && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Generated Content</label>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <textarea
                    className="w-full bg-transparent text-white border-none outline-none resize-none"
                    rows="6"
                    value={generatedContent}
                    onChange={(e) => setGeneratedContent(e.target.value)}
                  />
                </div>
              </div>
            )}

            {generatedImage && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Generated Image</label>
                <img 
                  src={generatedImage} 
                  alt="Generated" 
                  className="w-full max-w-md rounded-lg shadow-md border border-orange-500/30"
                />
              </div>
            )}

            <button
              onClick={savePost}
              disabled={loading.saving}
              className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed btn-glow"
            >
              {loading.saving ? '⏳ Saving...' : '💾 Save Post'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIGenerateModal;
